<!--
  @component ImportRecordList
  @description Reusable record list for import modal (used for both catalog results and file records)
-->
<script lang="ts">
  import { t } from '@/i18n';
  import type { Creator, UnifiedRecord } from '@types';

  export let records: UnifiedRecord[] = [];
  export let selectedRecords: Set<string> = new Set();
  export let showThumbnails = true;

  $: selectAllChecked = records.length > 0 && records.every((r) => selectedRecords.has(r.id));

  function toggleRecord(id: string) {
    if (selectedRecords.has(id)) {
      selectedRecords.delete(id);
    } else {
      selectedRecords.add(id);
    }
    selectedRecords = new Set(selectedRecords);
  }

  function toggleSelectAll() {
    if (selectAllChecked) {
      selectedRecords = new Set();
    } else {
      selectedRecords = new Set(records.map((r) => r.id));
    }
  }

  function formatCreators(record: UnifiedRecord): string {
    const creators = (record.item.creators || []) as Creator[];
    if (creators.length === 0) return '';

    return (
      creators
        .slice(0, 3)
        .map((c) => c.name || `${c.lastName || ''}, ${c.firstName || ''}`.trim())
        .join('; ') + (creators.length > 3 ? ' et al.' : '')
    );
  }
</script>

{#if records.length > 0}
  <div class="results-container">
    <div class="results-header">
      <label class="select-all">
        <input type="checkbox" checked={selectAllChecked} on:change={toggleSelectAll} />
        <span>{$t('import.selectAll')}</span>
      </label>
      <span class="results-count">
        {$t('import.recordsFound', { count: records.length })}
      </span>
    </div>

    <div class="results-list">
      {#each records as record (record.id)}
        <div
          class="result-item"
          class:selected={selectedRecords.has(record.id)}
          on:click={() => toggleRecord(record.id)}
          on:keydown={(e) => e.key === 'Enter' && toggleRecord(record.id)}
          role="button"
          tabindex="0"
        >
          <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-element-interactions -->
          <label class="result-checkbox" on:click|stopPropagation>
            <input
              type="checkbox"
              checked={selectedRecords.has(record.id)}
              on:change={() => toggleRecord(record.id)}
            />
          </label>
          {#if showThumbnails && record.thumbnail}
            <div class="result-thumbnail">
              <img src={record.thumbnail} alt="" />
            </div>
          {/if}
          <div class="result-content">
            <div class="result-title">
              {record.item.title || $t('import.untitled')}
            </div>
            <div class="result-meta">
              {#if formatCreators(record)}
                <span class="result-creators">{formatCreators(record)}</span>
              {/if}
              {#if record.item.date}
                <span class="result-date">{record.item.date}</span>
              {/if}
              {#if record.item.publisher}
                <span class="result-publisher">{record.item.publisher}</span>
              {/if}
            </div>
            <div class="result-identifiers">
              {#if record.identifiers.ppn}
                <span class="result-id">PPN: {record.identifiers.ppn}</span>
              {/if}
              {#if record.identifiers.isbn}
                <span class="result-isbn">ISBN: {record.identifiers.isbn}</span>
              {/if}
              {#if record.identifiers.issn}
                <span class="result-issn">ISSN: {record.identifiers.issn}</span>
              {/if}
            </div>
          </div>
          <div class="result-type">
            {$t(`item.types.${record.item.itemType}`) || record.item.itemType}
          </div>
        </div>
      {/each}
    </div>
  </div>
{/if}

<style>
  .results-container {
    min-height: 200px;
  }

  .results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid var(--color-border, #555);
    margin-bottom: 8px;
  }

  .select-all {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .results-count {
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
  }

  .results-list {
    max-height: 300px;
    overflow-y: auto;
  }

  .result-item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 4px;
    transition: background-color 0.15s;
    cursor: pointer;
  }

  .result-item:hover {
    background: var(--color-bg-hover, #444);
  }

  .result-item.selected {
    background: rgba(92, 124, 250, 0.15);
  }

  .result-checkbox {
    flex-shrink: 0;
    padding-top: 2px;
  }

  .result-thumbnail {
    flex-shrink: 0;
    width: 40px;
    height: 56px;
    background: var(--color-bg-primary, #2b2b2b);
    border-radius: 2px;
    overflow: hidden;
  }

  .result-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .result-content {
    flex: 1;
    min-width: 0;
  }

  .result-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary, #e8e8e8);
    margin-bottom: 4px;
    line-height: 1.3;
  }

  .result-meta {
    font-size: 13px;
    color: var(--color-text-secondary, #aaa);
    margin-bottom: 4px;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .result-meta span:not(:last-child)::after {
    content: '|';
    margin-left: 8px;
    color: var(--color-border, #555);
  }

  .result-identifiers {
    font-size: 12px;
    color: var(--color-text-muted, #888);
    display: flex;
    gap: 12px;
  }

  .result-type {
    flex-shrink: 0;
    font-size: 11px;
    color: var(--color-text-muted, #888);
    background: var(--color-bg-tertiary, #4a4a4a);
    padding: 4px 8px;
    border-radius: 4px;
    text-transform: capitalize;
  }

  @media (max-width: 768px) {
    .result-type {
      display: none;
    }
  }
</style>
